/**
 * Authentication Context
 * Manages user authentication state and provides authentication methods
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';

const AuthContext = createContext();

/**
 * Authentication Provider Component
 * Wraps the app and provides authentication state and methods
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const hasCheckedAuth = useRef(false);

  // Check if user is authenticated on app load
  useEffect(() => {
    // Prevent multiple authentication checks
    if (hasCheckedAuth.current) return;
    
    const checkAuth = async () => {
      if (token) {
        try {
          const userData = await apiFetch('/api/auth/profile', {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(userData.user);
        } catch (error) {
          console.error('Authentication check failed:', error);
          // Clear authentication state on error
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
      hasCheckedAuth.current = true;
    };

    checkAuth();
  }, [token]); // Include token in dependencies to satisfy ESLint

  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login response
   */
  const login = async (email, password) => {
    try {
      const response = await apiFetch('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password })
      });

      const { token: newToken, user: userData } = response;
      
      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login failed:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Register new user (creates pending account)
   * @param {Object} userData - User registration data
   * @returns {Promise<Object>} Registration response
   */
  const register = async (userData) => {
    try {
      // Remove confirmPassword from the data sent to API
      const { confirmPassword, ...registrationData } = userData;
      
      const response = await apiFetch('/api/auth/register', {
        method: 'POST',
        body: JSON.stringify(registrationData)
      });

      // Registration successful but no token (pending approval)
      return { success: true, message: response.message };
    } catch (error) {
      console.error('Registration failed:', error);
      throw error; // Re-throw to handle in component
    }
  };

  /**
   * Logout user and clear authentication state
   */
  const logout = async () => {
    try {
      if (token) {
        await apiFetch('/api/auth/logout', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setToken(null);
      localStorage.removeItem('token');
    }
  };

  /**
   * Update user profile
   * @param {Object} updates - Profile updates
   * @returns {Promise<Object>} Update response
   */
  const updateProfile = async (updates) => {
    try {
      const response = await apiFetch('/api/auth/profile', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(updates)
      });

      setUser(response.user);
      return { success: true, user: response.user };
    } catch (error) {
      console.error('Profile update failed:', error);
      return { success: false, error: error.message };
    }
  };

  /**
   * Change user password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise<Object>} Password change response
   */
  const changePassword = async (currentPassword, newPassword) => {
    try {
      await apiFetch('/api/auth/change-password', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword, newPassword })
      });

      return { success: true };
    } catch (error) {
      console.error('Password change failed:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    updateProfile,
    changePassword
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use authentication context
 * @returns {Object} Authentication context value
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
